/**
 * useAuthorizationPOC Hook
 * Manages POC certification loading and filtering for authorizations
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { POCCertificationsService } from '../../../../services/pocCertifications.service';
import { POCCertification } from '../types';

export interface UseAuthorizationPOCOptions {
  patientId?: string;
  expanded: boolean;
  discipline?: string;
}

export interface UseAuthorizationPOCResult {
  pocCertifications: POCCertification[];
  loading: boolean;
  error: string | null;
  filteredPOCCertifications: POCCertification[];
}

export function useAuthorizationPOC({
  patientId,
  expanded,
  discipline
}: UseAuthorizationPOCOptions): UseAuthorizationPOCResult {
  const [pocCertifications, setPOCCertifications] = useState<POCCertification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Deduplication ref to prevent duplicate loads for the same patient (React Strict Mode fix)
  const hasLoadedPOCRef = useRef<string | null>(null);

  // Load POC Certifications for linking
  useEffect(() => {
    const fetchPOCCertifications = async () => {
      if (!patientId) return;

      // Prevent duplicate loads for the same patient (React Strict Mode double-invoke fix)
      if (hasLoadedPOCRef.current === patientId) {
        return;  // Already loaded this patient's POC certifications
      }

      try {
        setLoading(true);
        setError(null);
        const response = await POCCertificationsService.listPOCCertifications(patientId);
        setPOCCertifications(response.certifications || []);

        // Mark as loaded for this patient
        hasLoadedPOCRef.current = patientId;
      } catch (err) {
        console.error('Error loading POC certifications:', err);
        setError(err instanceof Error ? err.message : 'Failed to load POC certifications');
        // Still mark as loaded on error to prevent infinite retries
        hasLoadedPOCRef.current = patientId;
      } finally {
        setLoading(false);
      }
    };

    if (expanded && patientId) {
      fetchPOCCertifications();
    }
  }, [expanded, patientId]);

  // Filter POC certifications by discipline
  const filteredPOCCertifications = useMemo(() => {
    if (!discipline) return pocCertifications;
    return pocCertifications.filter(poc => poc.discipline === discipline);
  }, [pocCertifications, discipline]);

  return {
    pocCertifications,
    loading,
    error,
    filteredPOCCertifications
  };
}
